// Chat — Jeff's chat surface (2026-11-03; rebuilt to DNA v6 on his 11/03 verdict:
// v1 shipped as a dark glass chat — the token file FORBIDS dark backgrounds and
// saturated fills — and it died silently signed-out with no first-move CTA).
//
// What this page is: a real chat with Kit. Left rail = past chats (start a new
// one anytime). While a chat is open, the right rail lists the .md reference
// files pulled in as context. At any point, "Make this a project" hands the
// thread to Kit: it creates a goal row state=INTAKE (Kit's intake pipeline owns
// it from there) AND lands it on the desk's On hold for Jeff's kickoff —
// exactly the intake→desk flow, driven from the chat itself.
//
// Design rules honored (design/tokens.js): white canvas cards on the dot-grid,
// ink text, hairline os-border, status colors as words only, gold-tint chip for
// the waiting-on-Kit "Make this a project" accent, mono for meta. No dark
// surfaces, no blue/emerald/amber fills, no entrance animations.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AuthGate } from "@/screens/cc";
import AutoDocLinks from "@/lib/AutoDocLinks";
import { openInDocs } from "@/lib/docLinkify";

const API = {
  session: "/api/session",
  list: "/api/chat/threads",
  create: "/api/chat/threads",
  thread: (id) => `/api/chat/threads/${id}`,
  send: (id) => `/api/chat/threads/${id}/messages`,
  project: (id) => `/api/chat/threads/${id}/make-project`,
  upload: (id) => `/api/chat/threads/${id}/attachments`,
  file: (aid) => `/api/chat/attachment/${aid}`,
};

async function api(path, body) {
  const res = await fetch(path, body
    ? { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }
    : {});
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Request failed (${res.status})`);
  return data;
}

// Light formatting for chat text (Jeff 11/03: "format a little bit better"):
// **bold** and `code` only — paragraphs are kept as typed (whitespace-pre-wrap).
// Built as React nodes; no HTML injection anywhere.
export function inlineFmt(text) {
  const parts = String(text).split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**") && p.length > 4) {
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    }
    if (p.startsWith("`") && p.endsWith("`") && p.length > 2) {
      return (
        <code key={i} className="rounded bg-os-wash px-1 py-0.5 font-mono text-[0.8125rem] text-os-ink">
          {p.slice(1, -1)}
        </code>
      );
    }
    return p;
  });
}

export function ChatList({ threads, activeId, onOpen, onNew, busy }) {
  return (
    <div className="flex h-full w-52 shrink-0 flex-col border-r border-os-border bg-os-wash" data-testid="chat-list">
      <button
        data-testid="chat-new"
        onClick={onNew}
        disabled={busy}
        className="m-3 rounded-control border border-os-border-strong bg-white px-3 py-2 text-[0.875rem] font-medium text-os-ink hover:border-os-ink disabled:opacity-40"
      >
        + New chat
      </button>
      <div className="px-3 pb-1 text-[0.6875rem] font-medium uppercase tracking-[0.08em] text-os-ink-4">
        Past chats
      </div>
      <div className="flex-1 overflow-y-auto px-2 pb-3">
        {threads.length === 0 && (
          <div className="px-2 py-2 text-[0.8125rem] text-os-ink-3">No chats yet — start your first one.</div>
        )}
        {threads.map((t) => (
          <button
            key={t.slug}
            data-testid={`chat-thread-${t.slug}`}
            onClick={() => onOpen(t.slug)}
            className={`mb-0.5 block w-full rounded-chip border px-2.5 py-1.5 text-left text-[0.8125rem] leading-[1.35] ${
              t.slug === activeId
                ? "border-os-border-strong bg-white text-os-ink"
                : "border-transparent text-os-ink-2 hover:border-os-border hover:text-os-ink"
            }`}
            title={t.title || t.payload?.title || "New chat"}
          >
            <span
              className="block break-words"
              // 2-line cap via direct -webkit-box (Jeff 11-04: "2 lines instead of 3").
              // The tailwind line-clamp plugin's CSS was flattened by the prod CSS
              // minifier (lost display:-webkit-box → clamp silently dead → 3 lines).
              // Inline styles can't be eaten by the minify pipeline.
              style={{ display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, overflow: "hidden" }}
            >
              {t.title || t.payload?.title || "New chat"}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function RefSidebar({ files, onOpenRef }) {
  return (
    <div className="flex h-full w-56 shrink-0 flex-col border-l border-os-border bg-os-wash" data-testid="chat-refs">
      <div className="px-3 py-2 text-[0.6875rem] font-medium uppercase tracking-[0.08em] text-os-ink-4">Reference files</div>
      <div className="flex-1 overflow-y-auto px-2 pb-3">
        {(!files || files.length === 0) && (
          <div className="px-2 py-1 text-[0.8125rem] text-os-ink-3">Pulled in as the chat needs them</div>
        )}
        {(files || []).map((f) => (
          <button
            key={f.path}
            type="button"
            data-testid={`chat-ref-${(f.name || f.path).replace(/[^a-z0-9]/gi, "-")}`}
            onClick={() => onOpenRef && onOpenRef(f)}
            title={`${f.path}\nOpen in the reader`}
            className="mb-0.5 block w-full rounded-chip px-2 py-1 text-left text-[0.8125rem] text-os-ink-2 hover:bg-white hover:text-os-ink"
          >
            <span className="block break-words leading-[1.35]">
              <span className="mr-1 font-mono text-[0.6875rem] text-os-ink-3">md</span>
              {f.name || f.path.split("/").pop()}
            </span>
          </button>
        ))}
        {(files || []).length > 0 && (
          <div className="px-2 pt-1 text-[0.6875rem] text-os-ink-4">Click a file to open it in the reader.</div>
        )}
      </div>
    </div>
  );
}

export function ChatMessage({ m }) {
  const mine = m.role === "jeff";
  // Per-message copy (Jeff 11-04: "copy a single chat entry so we can copy
  // and paste elsewhere") — hover reveals a small Copy control on every
  // message; it copies the RAW text (no markdown artifacts) and flips to
  // "Copied" for a beat. navigator.clipboard needs a secure context; file://
  // or http fall back to the hidden-textarea trick.
  const copyRef = React.useRef(null);
  const [copied, setCopied] = React.useState(false);
  const raw = String(m.text ?? m.payload?.text ?? "");
  const doCopy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(raw);
      } else {
        const ta = document.createElement("textarea");
        ta.value = raw;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      if (copyRef.current) clearTimeout(copyRef.current);
      copyRef.current = setTimeout(() => setCopied(false), 1400);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className={`group relative flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.slug || m.id}`}>
      <div
        className={`max-w-[75%] whitespace-pre-wrap rounded-card px-4 py-2.5 text-[0.875rem] leading-relaxed ${
          mine ? "bg-os-ink text-white" : "border border-os-border bg-white text-os-ink"
        }`}
      >
        <AutoDocLinks text={raw} renderGap={inlineFmt} />
        {(m.attachments || m.payload?.attachments || []).length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="chat-msg-attachments">
            {(m.attachments || m.payload?.attachments || []).map((a) => (
              <a
                key={a.id}
                href={API.file(a.id)}
                target="_blank"
                rel="noreferrer"
                data-testid={`chat-att-${a.id}`}
                className={`rounded-chip border px-2 py-1 text-[0.6875rem] ${
                  mine
                    ? "border-white/30 text-white/85 hover:border-white/60"
                    : "border-os-border bg-os-wash text-os-ink-2 hover:border-os-border-strong"
                }`}
              >
                {(a.mime || "").startsWith("image/") ? "🖼" : "📄"} {a.filename}
              </a>
            ))}
          </div>
        )}
      </div>
      {raw.trim() && (
        <button
          type="button"
          data-testid={`chat-copy-${m.slug || m.id}`}
          onClick={doCopy}
          aria-label="Copy this message"
          className={`absolute top-1 ${mine ? "right-[76%] mr-1" : "left-[76%] ml-1"} rounded-chip border px-1.5 py-0.5 font-mono text-[0.6875rem] opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 ${
            mine
              ? "border-white/30 bg-white text-os-ink hover:bg-os-wash"
              : "border-os-border bg-white text-os-ink-3 hover:text-os-ink"
          } ${copied ? "!opacity-100" : ""}`}
        >
          {copied ? "Copied" : "Copy"}
        </button>
      )}
    </div>
  );
}

// Engine picker (Jeff 2026-11-03): Ollama proxy (the portfolio default pool) or
// OpenRouter — which lands on the discounted weekly-rotation 'deal' chat model.
// Rendered as the DeskPage segmented control — never colored pills.
export function EnginePicker({ engine, onPick, disabled }) {
  const opts = [
    { key: "ollama", label: "Ollama", testid: "chat-engine-ollama" },
    { key: "openrouter", label: "OpenRouter (discounted)", testid: "chat-engine-openrouter" },
  ];
  return (
    <div
      className="flex items-center rounded-control border border-os-border bg-white p-0.5"
      data-testid="chat-engine-picker"
      role="radiogroup"
      aria-label="Answer engine"
    >
      {opts.map((o) => (
        <button
          key={o.key}
          role="radio"
          aria-checked={engine === o.key}
          data-testid={o.testid}
          disabled={disabled}
          onClick={() => onPick(o.key)}
          className={`rounded-[7px] px-2.5 py-1 text-[0.8125rem] ${
            engine === o.key ? "bg-os-ink text-white" : "text-os-ink-3 hover:text-os-ink"
          } disabled:opacity-40`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function MakeProjectBar({ onMake, done, busy, projectTitle, variant = "bar" }) {
  const fullText = `Project created — it's on your desk (On hold) for approval${projectTitle ? `: ${projectTitle}` : ""}`;
  // "chip" = compact control for the composer's button row (Jeff 11/04: make-
  // project rides NEXT TO SEND, not on its own row). "bar" = the standalone
  // confirmation line (unit-tested contract).
  if (variant === "chip") {
    return done ? (
      <span
        data-testid="chat-make-done"
        title={fullText}
        className="rounded-chip border border-os-border bg-os-wash px-2.5 py-1.5 text-[0.8125rem] font-medium text-os-green"
      >
        ✓ On your desk
      </span>
    ) : (
      <button
        data-testid="chat-make-project"
        onClick={onMake}
        disabled={busy}
        className="rounded-control border border-os-bronze bg-os-gold-tint px-3 py-1.5 text-[0.8125rem] font-medium text-os-ink hover:border-os-bronze-strong disabled:opacity-40"
      >
        Make this a project
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2 border-t border-os-border px-3 py-2" data-testid="chat-make-bar">
      {done ? (
        <div className="text-[0.8125rem] font-medium text-os-green" data-testid="chat-make-done">
          {fullText}
        </div>
      ) : (
        <button
          data-testid="chat-make-project"
          onClick={onMake}
          disabled={busy}
          className="rounded-control border border-os-bronze bg-os-gold-tint px-3 py-1.5 text-[0.8125rem] font-medium text-os-ink hover:border-os-bronze-strong disabled:opacity-40"
        >
          Make this a project
        </button>
      )}
    </div>
  );
}

// Custom attach icon (Jeff 11/04: not the generic 📎 emoji) — a clipped-paper
// glyph drawn to the design system: 1.5px ink strokes, no fill. Same 16-box
// as the shell's icons, inherits currentColor.
export function ClipIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" fill="none" aria-hidden="true"
      className={className} data-testid="chat-clip-icon">
      <path
        d="M12.9 7.2l-4.6 4.6a3.1 3.1 0 0 1-4.4-4.4l5.3-5.3a2.1 2.1 0 0 1 3 3L6.9 10.4a1.05 1.05 0 0 1-1.5-1.5l4.4-4.4"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

export default function ChatPage({ onFlash }) {
  const [signedIn, setSignedIn] = useState(null); // null = checking; only explicit false = signed out
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [refs, setRefs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [madeProject, setMadeProject] = useState(null);
  const [engine, setEngine] = useState("ollama");
  const [replyMeta, setReplyMeta] = useState(null); // {engine, model} of Kit's last answer
  const [pendingFiles, setPendingFiles] = useState([]); // [{id, filename, mime}]
  const threadTitle = useMemo(() => {
    const t = (threads || []).find((x) => x.slug === activeId);
    return (t && (t.title || t.payload?.title)) || "";
  }, [threads, activeId]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const bottomRef = useRef(null);
  const taRef = useRef(null);
  // Composer constants (Jeff 11/03): growth mechanics live with send() below
  // (v3.3 uncontrolled composer); 4000 chars = the backend's cap, so the
  // counter never lies.
  const CHARS_MAX = 4000;
  const MIN_H = 72;
  const MAX_H = 340;

  useEffect(() => {
    // Honest signed-out state (Jeff 11/03 "it doesn't function"): v1 silently
    // 401'd everything and rendered a dead page. Only an explicit authed=false
    // is trusted — anything else (error, odd shape) assumes signed in and lets
    // real actions surface real errors.
    let alive = true;
    fetch(API.session)
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => { if (alive) setSignedIn(d.authed !== false); })
      .catch(() => { if (alive) setSignedIn(true); });
    return () => { alive = false; };
  }, []);

  const loadThreads = useCallback(async () => {
    const d = await api(API.list);
    setThreads(d.items || []);
    return d.items || [];
  }, []);

  useEffect(() => { if (signedIn) loadThreads().catch(() => {}); }, [loadThreads, signedIn]);

  const openThread = useCallback(async (slug) => {
    setActiveId(slug);
    setMadeProject(null);
    setPendingFiles([]);
    try {
      const d = await api(API.thread(slug));
      setMessages(d.items || []);
      setRefs((d.meta || {}).refs || []);
    } catch {
      setMessages([]);
      setRefs([]);
    }
  }, []);

  const bootedRef = useRef(false);
  // Desk ÃÂ¢ÃÂÃÂ chat leg of the loop (Jeff 11-04): clicking "open the shaping chat"
  // on a desk card lands here with ?thread= and opens that conversation.
  useEffect(() => {
    if (bootedRef.current || !signedIn) return;
    bootedRef.current = true;
    const th = new URLSearchParams(window.location.search).get("thread");
    if (!th) return;
    openThread(th).catch(() => {});
    window.history.replaceState({}, "", window.location.pathname);
  }, [signedIn, openThread]);

  const newChat = useCallback(async () => {
    setBusy(true);
    try {
      const d = await api(API.create, { title: "New chat" });
      await loadThreads();
      setActiveId(d.slug);
      setMessages([]);
      setRefs([]);
      setMadeProject(null);
      setPendingFiles([]);
    } catch (e) {
      onFlash && onFlash(e.message || "Couldn't start a chat");
    } finally {
      setBusy(false);
    }
  }, [loadThreads, onFlash]);

  const pickFile = useCallback(() => {
    if (!activeId || busy) return;
    fileInputRef.current?.click();
  }, [activeId, busy]);

  const onFilesChosen = useCallback(async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6);
    e.target.value = ""; // allow re-picking the same file
    if (!files.length || !activeId) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const buf = await f.arrayBuffer();
        let bin = "";
        const bytes = new Uint8Array(buf);
        const chunk = 0x8000;
        for (let i = 0; i < bytes.length; i += chunk) {
          bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
        }
        const d = await api(API.upload(activeId), {
          filename: f.name, mime: f.type || "application/octet-stream",
          bytes_b64: btoa(bin),
        });
        uploaded.push({ id: d.attach_id, filename: f.name, mime: f.type || "" });
      }
      setPendingFiles((p) => [...p, ...uploaded]);
      onFlash && onFlash(`${uploaded.length} file${uploaded.length === 1 ? "" : "s"} attached`);
    } catch (err) {
      onFlash && onFlash(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  }, [activeId, onFlash]);

  // Send (v3.3): the composer is UNCONTROLLED — the text lives in the field,
  // not in React state — so dictation tools (VoiceInk) that set the field's
  // value directly are never fought or wiped by a re-render. Only a typed
  // character count rides state (counter + growth). Send reads the real field.
  const [draftLen, setDraftLen] = useState(0);
  const [status, setStatus] = useState({ kind: "idle" }); // idle | sending | error
  const syncHeight = useCallback(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    const next = Math.min(Math.max(ta.scrollHeight, MIN_H), MAX_H);
    ta.style.height = `${next}px`;
    ta.style.overflowY = "auto"; // scrollable inside the cap no matter what
  }, []);
  useEffect(() => { syncHeight(); }, [draftLen, activeId, syncHeight]);

  const send = useCallback(async () => {
    const ta = taRef.current;
    const text = (ta ? ta.value : "").trim();
    if (!activeId || busy) return;
    if (!text && pendingFiles.length === 0) {
      setStatus({ kind: "error", why: "Type a message first — the box is empty." });
      return;
    }
    setBusy(true);
    setStatus({ kind: "sending" });
    if (ta) ta.value = "";
    setDraftLen(0);
    const atts = pendingFiles;
    setPendingFiles([]);
    // optimistic: my message lands instantly (attachments render once confirmed)
    setMessages((ms) => [...ms, { id: `local-${Date.now()}`, role: "jeff", payload: { text } }]);
    try {
      const d = await api(API.send(activeId), { text, engine, attachments: atts.map((a) => a.id) });
      setMessages((d.items || []));
      setRefs((d.meta || {}).refs || []);
      setReplyMeta((d.meta || {}).replyMeta || null);
      setStatus({ kind: "idle" });
      loadThreads().catch(() => {});
    } catch (e) {
      const why = e.message || "Message didn't send";
      setStatus({ kind: "error", why });
      if (ta) { ta.value = text; setDraftLen(text.length); } // don't lose the words on failure
      setPendingFiles(atts);
    } finally {
      setBusy(false);
    }
  }, [activeId, busy, engine, pendingFiles, loadThreads]);

  const makeProject = useCallback(async () => {
    if (!activeId || busy) return;
    setBusy(true);
    try {
      const d = await api(API.project(activeId), {});
      setMadeProject(d.title || d.slug || true);
      onFlash && onFlash("Project created — on your desk, On hold, awaiting your approval");
    } catch (e) {
      onFlash && onFlash(e.message || "Couldn't create the project");
    } finally {
      setBusy(false);
    }
  }, [activeId, busy, onFlash]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  if (signedIn === false) {
    // Signed out: the OS-standard sign-in card (pass phrase → cookie → reload),
    // same as every board page. Never a dead silent page, never a dead-end card.
    return (
      <div className="flex h-full min-w-0 items-center justify-center" data-testid="chat-page">
        <AuthGate />
      </div>
    );
  }

  return (
    <div className="flex h-full min-w-0" data-testid="chat-page">
      <ChatList threads={threads} activeId={activeId} onOpen={openThread} onNew={newChat} busy={busy} />
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Fixed header: the thread title is a headline, NOT stream content (Jeff
            11/04: the center pane scrolls, sidebars never). 2-line cap inline so
            the prod minifier can't flatten the -webkit-box clamp away. */}
        {activeId && (
          <div className="shrink-0 border-b border-os-border bg-white px-4 py-2" data-testid="chat-header">
            <div className="flex items-center justify-between gap-3">
              <div
                className="text-[0.9375rem] font-semibold leading-[1.35] text-os-ink"
                data-testid="chat-header-title"
                style={{ display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, overflow: "hidden" }}
                title={threadTitle}
              >
                {threadTitle}
              </div>
              {/* Latest (Jeff 11-04: get to the composer fast — with a long
                  stream, one click scrolls to the bottom AND focuses the
                  input, so typing starts immediately). */}
              <button
                type="button"
                data-testid="chat-jump-latest"
                onClick={() => {
                  bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
                  const ta = taRef.current;
                  if (ta) { ta.focus(); }
                }}
                disabled={busy}
                title="Jump to the latest message and focus the composer"
                className="shrink-0 rounded-chip border border-os-border bg-white px-2 py-1 font-mono text-[0.6875rem] text-os-ink-3 hover:border-os-border-strong hover:text-os-ink disabled:opacity-40"
              >
                ↓ Latest
              </button>
            </div>
          </div>
        )}
        {/* The ONLY scroller on the page: the message stream. h-0 + flex-1 pins
            it to the leftover height; sidebars/composer stay put. */}
        <div className="h-0 flex-1 space-y-2.5 overflow-y-auto px-4 py-3" data-testid="chat-stream">
          {messages.length === 0 && activeId && (
            <div className="py-8 text-center text-[0.875rem] text-os-ink-2">
              Say what you want built — make it a project whenever it's shaped.
            </div>
          )}
          {messages.map((m, i) => <ChatMessage key={m.slug || m.id || i} m={m} />)}
          {status.kind === "sending" && (
            // Kit is working — visible IN the chat, never silence (Jeff 11/04:
            // "seems like nothing's happening" — the old page sat blank through
            // the model call). A quiet ink-3 line, pulsing gently.
            <div className="flex justify-start" data-testid="chat-thinking">
              <div className="animate-pulse rounded-chip border border-os-border bg-white px-3 py-1.5 text-[0.8125rem] text-os-ink-3">
                Kit is thinking…
              </div>
            </div>
          )}
          {status.kind === "error" && (
            // Errors live in the chat, in plain words, and stay until the next send.
            <div className="flex justify-start" data-testid="chat-error">
              <div className="max-w-[75%] rounded-card border border-os-border bg-white px-3.5 py-2 text-[0.875rem] text-os-red">
                {status.why}
                <span className="ml-1.5 text-[0.8125rem] text-os-ink-3">Your text is back in the box — try Send again.</span>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
        {!activeId && (
          // Cold start: the ONE move, impossible to miss (Jeff 11/03: he couldn't
          // tell how to begin — v1 showed only a disabled composer).
          <div className="flex flex-1 items-center justify-center p-6">
            <div className="rounded-card border border-os-border bg-white px-8 py-7 text-center">
              <div className="text-[0.6875rem] font-medium uppercase tracking-[0.08em] text-os-ink-4">Chats</div>
              <div className="mt-1 text-[0.9375rem] font-semibold text-os-ink">Nothing here yet</div>
              <p className="mt-1 text-[0.875rem] text-os-ink-2">
                Start a chat with Kit — it keeps its own history.
              </p>
              <button
                data-testid="chat-new-hero"
                onClick={() => { if (!busy) newChat(); }}
                disabled={busy}
                className="mt-4 rounded-control bg-os-ink px-4 py-2 text-[0.875rem] font-medium text-white hover:opacity-90 disabled:opacity-40"
              >
                + New chat
              </button>
            </div>
          </div>
        )}
        {activeId && replyMeta && (
          // Persistent meta line (Jeff 11-04: show WHICH model + a Discord-style
          // context meter "how much context has been put in out of how much").
          // Rides directly above the composer, mono, ink-3. Model = what
          // actually answered; ctx = chars÷4 estimate into the 64k window;
          // real provider token counts win when the provider reported them.
          <div className="flex items-center justify-between gap-3 px-3 pb-0.5 pt-1 font-mono text-[0.6875rem] text-os-ink-3" data-testid="chat-reply-meta">
            <div className="min-w-0 truncate" data-testid="chat-reply-meta-model">
              {replyMeta.engine === "fallback"
                ? `fallback script — engines tried: ${(replyMeta.attempts || []).join(" then ") || "none"}`
                : `model: ${replyMeta.model || "?"} · via ${replyMeta.engine === "openrouter" ? "OpenRouter" : "Ollama"}`}
              {replyMeta.tokens ? ` · ${replyMeta.tokens.prompt + replyMeta.tokens.completion} tok` : ""}
            </div>
            <div className="shrink-0" data-testid="chat-ctx-meter">
              {replyMeta.ctx && replyMeta.ctx.max
                ? `ctx ${replyMeta.ctx.used.toLocaleString()}/${(replyMeta.ctx.max / 1000).toFixed(0)}k (${Math.round((replyMeta.ctx.used / replyMeta.ctx.max) * 100)}%)`
                : "ctx —"}
            </div>
          </div>
        )}
        {activeId && (
          <div className="border-t border-os-border bg-white px-3 py-2.5">
            <div className="flex items-end gap-2">
              {/* hidden input drives the paperclip */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                data-testid="chat-file-input"
                className="hidden"
                onChange={onFilesChosen}
              />
              {/* Growing composer, UNCONTROLLED (Jeff 11/04: VoiceInk dictation
                  must land and stay — no state can wipe it). 3 rows at rest,
                  grows to 340px cap, scrolls inside it. Shift+Return = hard
                  return; Return = send. 4000-char honest ceiling, advisory. */}
              <textarea
                ref={taRef}
                data-testid="chat-input"
                defaultValue=""
                onInput={(e) => setDraftLen(e.currentTarget.value.length)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                  // Shift+Enter → hard return, inserted natively by the textarea
                }}
                placeholder="Type or dictate…  (Shift+Return for a new line)"
                disabled={!activeId || busy}
                rows={3}
                className="min-w-0 flex-1 resize-none rounded-control border border-os-border bg-white px-3 py-2 text-[0.875rem] leading-relaxed text-os-ink placeholder:text-os-ink-4 focus:border-os-border-strong focus:outline-none"
              />
              <div className="flex shrink-0 flex-col items-stretch gap-1">
                <button
                  data-testid="chat-send"
                  onClick={send}
                  disabled={!activeId || busy}
                  className="rounded-control bg-os-ink px-4 py-2 text-[0.8125rem] font-medium text-white hover:opacity-90 disabled:opacity-40"
                >
                  Send
                </button>
                <div
                  data-testid="chat-char-count"
                  className={`text-center font-mono text-[0.6875rem] ${draftLen > CHARS_MAX - 200 ? "text-os-amber" : "text-os-ink-4"}`}
                  title={`${CHARS_MAX} characters is the limit — it matches what the chat keeps`}
                >
                  {CHARS_MAX - draftLen}
                </div>
              </div>
            </div>
            {/* Controls row — everything lower down (Jeff 11/04): engine picker,
                attach, make-project next to Send-side of the row. */}
            <div className="mt-2 flex items-center justify-between gap-2" data-testid="chat-controls-row">
              <div className="flex items-center gap-2">
                <EnginePicker engine={engine} onPick={setEngine} disabled={busy} />
                <button
                  data-testid="chat-attach"
                  onClick={pickFile}
                  disabled={!activeId || busy || uploading}
                  title="Attach files"
                  className="rounded-control border border-os-border bg-white p-2 text-os-ink-3 hover:border-os-border-strong hover:text-os-ink disabled:opacity-40"
                >
                  {uploading ? <ClipIcon className="animate-pulse" /> : <ClipIcon />}
                </button>
              </div>
              <div className="flex items-center gap-2">
                {pendingFiles.length > 0 && (
                  <span className="font-mono text-[0.6875rem] text-os-ink-3">
                    {pendingFiles.length} file{pendingFiles.length === 1 ? "" : "s"} attached
                  </span>
                )}
                <MakeProjectBar variant="chip" onMake={makeProject} done={!!madeProject} busy={busy} projectTitle={typeof madeProject === "string" ? madeProject : ""} />
              </div>
            </div>
          </div>
        )}
      </div>
      <RefSidebar
        files={refs}
        onOpenRef={(f) => { openInDocs(f.path); }}
      />
    </div>
  );
}